import type { ReactNode } from "react";
import type { Ear } from "./tonalTest";
import type { AgeCategory, Gender, HeadphoneConnection, HeadphoneType } from "./demographics";

// ── Test steps ────────────────────────────────────────────────────────────────
export type VerbalTestStep = "start" | "params" | "volume" | "calibration" | "ear" | "srt" | "wrs" | "result";
export type VerbalCalibPhase = "idle" | "playing" | "choosing";


// ── Per-word response recorded during a test ─────────────────────────────────
export type VerbalWordRecord = {
  word: string;
  dB: number;
  heard: boolean;
  response?: string; // typed or recognised text (mic / text-input mode)
};

// ── Per-ear result object ─────────────────────────────────────────────────────
export type VerbalEarResult = {
  srt: number | null;      // Speech Reception Threshold in dB HL (null = not determinable)
  wrs: number | null;      // Word Recognition Score 0-100 % (null = not tested)
  srt_words: VerbalWordRecord[];
  wrs_words: VerbalWordRecord[];
};

// ── Full result payload (serialised to DB as JSON) ────────────────────────────
export type VerbalTestResultPayload = {
  srt: { left: number | null; right: number | null };
  wrs: { left: number | null; right: number | null };
  word_results: Record<Ear, VerbalEarResult>;
};

// ── API request body ──────────────────────────────────────────────────────────
export type AddVerbalTestResultRequest = {
  iduser: number | null;
  ip_address: null;
  web_agent: string;
  result: string;
  test_params: string;
};

// ── API response item (from server) ──────────────────────────────────────────
export type VerbalTestResultItem = {
  idverbalresult: number;
  iduser: number;
  username?: string | null;
  email?: string | null;
  result: string;
  created_at: string;
  deleted: number;
  ip_address: string;
  web_agent: string;
  test_params: string;
  ai_analysis: string;
};

// ── Test parameters (user-configurable) ──────────────────────────────────────
export type VerbalTestParameters = {
  useMicrophone: boolean;
  gender?: Gender;
  ageCategory?: AgeCategory;
  headphoneType?: HeadphoneType;
  headphoneConnection?: HeadphoneConnection;
};

// ── Adaptive progress per ear (mirrors EarProgress from pure-tone) ────────────
export type VerbalSRTProgress = {
  wordIndex: number;            // which word in the list we are on
  volIndex: number;             // current dB-level index in dBLevels array
  mode: "descending" | "ascending";
  lastHeardDb?: number;
};

// ── WRS phase state ───────────────────────────────────────────────────────────
export type VerbalWRSProgress = {
  wordIndex: number;
  totalWords: number;
  volIndex: number;             // fixed: SRT index + WRS_OFFSET_STEPS
  correctCount: number;
};

// ── Word-database entry types ─────────────────────────────────────────────────
export type SRTList = {
  id: number;
  name: string;
  type: string;
  words: string[];
};

export type WRSList = {
  id: number;
  name: string;
  type: string;
  phonetically_balanced: boolean;
  words: string[];
};

export type SentenceList = {
  id: number;
  name: string;
  type: string;
  description: string;
  sentences: string[];
};

export type WordDatabase = {
  version: string;
  language: string;
  description: string;
  srt_lists: SRTList[];
  wrs_lists: WRSList[];
  calibration_words: string[];
  wrs_sentence_lists: SentenceList[];
  srt_sentence_lists: SentenceList[];
};

// ── Result panel models ───────────────────────────────────────────────────────
export type VerbalResultPanelModel = {
  result: VerbalTestResultPayload | null;
  resultPanelRef?: React.RefObject<HTMLDivElement | null>;
  exportMenuRef?: React.RefObject<HTMLDivElement | null>;
  isExportMenuOpen?: boolean;
  isExporting?: boolean;
  exportFormat?: "pdf" | "png" | null;
  isExportDisabled?: boolean;
  showAnalysisButton?: boolean;
  isAnalysisDisabled?: boolean;
  onToggleExportMenu?: () => void;
  onExport?: (format: "pdf" | "png") => void;
  onAnalyze?: () => void;
  subtitle?: ReactNode;
  onNewTest?: () => void;
};

// ── SpeechRecognition Web API declarations ───────────────────────────────────
declare global {
  interface SpeechRecognitionAlternative {
    readonly transcript: string;
    readonly confidence: number;
  }
  interface SpeechRecognitionResult {
    readonly length: number;
    [index: number]: SpeechRecognitionAlternative;
  }
  interface SpeechRecognitionResultList {
    readonly length: number;
    [index: number]: SpeechRecognitionResult;
  }
  interface SpeechRecognitionEvent extends Event {
    readonly results: SpeechRecognitionResultList;
  }
  interface SpeechRecognition extends EventTarget {
    lang: string;
    interimResults: boolean;
    maxAlternatives: number;
    onresult: ((event: SpeechRecognitionEvent) => void) | null;
    onerror: ((event: Event) => void) | null;
    onend: (() => void) | null;
    start(): void;
    stop(): void;
    abort(): void;
  }
  interface Window {
    SpeechRecognition: { new(): SpeechRecognition };
    webkitSpeechRecognition: { new(): SpeechRecognition };
  }
}

export const hasSpeechRecognitionSupport = (): boolean =>
  typeof window !== "undefined" &&
  ("SpeechRecognition" in window || "webkitSpeechRecognition" in window);

export const hasMicrophoneSupport = (): boolean =>
  typeof navigator !== "undefined" &&
  "mediaDevices" in navigator &&
  hasSpeechRecognitionSupport();

export  type VerbalTestParametersProps = {
  step: VerbalTestStep;
  useMicrophone: boolean;
  onToggleMicrophone: () => void;
  doWRS: boolean;
  onToggleDoWRS: () => void;
  doSRT: boolean;
  onToggleDoSRT: () => void;
  useSentences: boolean;
  onToggleUseSentences: () => void;
  extendedWRS: boolean;
  onToggleExtendedWRS: () => void;
  gender: Gender;
  ageCategory: AgeCategory;
  onChangeGender: (value: Gender) => void;
  onChangeAgeCategory: (value: AgeCategory) => void;
};

// export type VerbalTestStepsProps = {
//   step: VerbalTestStep;
//   onNewTest: () => void;
//   doSRT: boolean;
//   doWRS: boolean;
// };

export type VerbalPanelState =
  | { phase: "playing" }
  | { phase: "awaiting_mic" }
  | { phase: "awaiting_choice"; choices: string[] }
  | { phase: "feedback"; correct: boolean; correctWord: string };

export type SRTPanelProps = {
  ear: Ear;
  useMicrophone: boolean;
  useSentences: boolean;
  onComplete: (ear: Ear, srt: number, wordRecords: VerbalWordRecord[]) => void;
  onCancel: (partialWords: VerbalWordRecord[]) => void;
  user?: { admin?: boolean } | null;
};

export type WRSPanelProps = {
  ear: Ear;
  srtDb: number;
  useMicrophone: boolean;
  useSentences: boolean;
  extendedWRS: boolean;
  onComplete: (ear: Ear, wrs: number, wordRecords: VerbalWordRecord[]) => void;
  onCancel: (partialWords: VerbalWordRecord[]) => void;
  user?: { admin?: boolean } | null;
};


export type VerbalResultPanelProps = {
  result: VerbalTestResultPayload | null;
  resultPanelRef?: React.RefObject<HTMLDivElement | null>;
  onNewTest?: () => void;
  subtitle?: ReactNode;
  children?: ReactNode;
  exportMenuRef?: React.RefObject<HTMLDivElement | null>;
  isExportMenuOpen?: boolean;
  isExporting?: boolean;
  exportFormat?: "pdf" | "png" | null;
  isExportDisabled?: boolean;
  showAnalysisButton?: boolean;
  isAnalysisDisabled?: boolean;
  onToggleExportMenu?: () => void;
  onExport?: (format: "pdf" | "png") => void;
  onAnalyze?: () => void;
  onEmailResult?: () => void;
};

export type VerbalParamsNoticeProps = {
  onConfirmed: () => void;
  onOpenParamsModal: () => void;
  onNewTest: () => void;
  useMicrophone: boolean;
  doWRS: boolean;
  doSRT: boolean;
  useSentences: boolean;
  gender: Gender;
  ageCategory: AgeCategory;
  headphoneType: HeadphoneType;
  headphoneConnection: HeadphoneConnection;
  onChangeGender: (value: Gender) => void;
  onChangeAgeCategory: (value: AgeCategory) => void;
  onChangeHeadphoneType: (value: HeadphoneType) => void;
  onChangeHeadphoneConnection: (value: HeadphoneConnection) => void;
};